import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { FiChevronDown, FiChevronUp } from 'react-icons/fi'

// Posbírá všechny url z celého stromu menu (libovolná hloubka) — potřeba pro
// detekci "přesnější shody" u sourozeneckých položek se sdíleným prefixem.
const collectUrls = (items, acc = []) => {
    ;(items || []).forEach((item) => {
        if (item.url && item.url !== '#') acc.push(item.url)
        if (item.items) collectUrls(item.items, acc)
    })
    return acc
}

// Konfigurovaná url "Seznamu" je typicky /{zdroj}/list, ale detail/add stránky
// žijí přímo pod /{zdroj}/... (/zone/detail/5, ne /zone/list/detail/5) — kořen
// pro porovnání je proto bez koncového /list.
const listRoot = (url) => (url.endsWith('/list') ? url.slice(0, -'/list'.length) : url)

// Aktuální cesta patří "pod" url (detail/add/... podstránka bez vlastní položky
// v menu, např. /zone/detail/5 nebo /zone/add pod "Seznam" → /zone/list). Nezvýrazní
// se ale, pokud existuje JINÁ konkrétnější url v menu, která cestě sedí líp —
// jinak by se u /zone/stats rozsvítily najednou "Seznam" i "Statistiky".
const isNestedUnder = (pathname, url, allUrls) => {
    const base = listRoot(url)
    if (!pathname.startsWith(base + '/')) return false
    return !allUrls.some(
        (u) => u !== url && u.length > base.length && (pathname === u || pathname.startsWith(u + '/'))
    )
}

const matchesPath = (url, pathname, allUrls) =>
    !!url && url !== '#' && (pathname === url || isNestedUnder(pathname, url, allUrls))

// Pomocná funkce: rekurzivně prohledá strom položky a zjistí, zda obsahuje aktivní URL
// (včetně detail/add podstránek — viz matchesPath).
const isAnyChildActive = (items, currentPath, allUrls) => {
    if (!items || !Array.isArray(items) || items.length === 0) return false

    return items.some((item) => {
        if (matchesPath(item.url, currentPath, allUrls)) {
            return true
        }
        if (item.items && item.items.length > 0) {
            return isAnyChildActive(item.items, currentPath, allUrls)
        }
        return false
    })
}

// collapsed = sbalený stav sidebaru (jen ikony). Stejná logika jako u starého
// left-sidebar-1: text/šipka se schová, potomci se ukážou jako flyout při
// najetí myší místo akordeonu. Flyout se portáluje do body přes position:fixed,
// aby ho nemohl useknout overflow-y:auto na scrollovatelném nav sloupci.
const Item = ({ title, icon, url, active, items, children, collapsed = false }) => {
    const location = useLocation()
    const navigation = useSelector((s) => s.navigation)
    const dark = useSelector((s) => s.palettes?.background === 'dark')
    const hasChildren = (items && items.length > 0) || Boolean(children)

    // Celý strom menu jen pro rozpoznání "přesnější shody" u sourozenců (viz matchesPath).
    const allUrls = navigation.reduce((acc, menu) => collectUrls(menu.items, acc), [])

    // 1. Je aktivní přímo tato položka (nebo její detail/add podstránka)?
    const isDirectlyActive = active || matchesPath(url, location.pathname, allUrls)

    // 2. Je aktivní libovolný vnořený potomek (bez ohledu na hloubku zanoření)?
    const isChildActive = hasChildren && isAnyChildActive(items, location.pathname, allUrls)

    // Otevření akordeonu: pokud je aktivní tento prvek nebo jakýkoliv jeho potomek
    const [open, setOpen] = useState(false)

    // Flyout pro sbalený stav — nezávislý na akordeonu, ovládá se hoverem.
    const [flyoutOpen, setFlyoutOpen] = useState(false)
    const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0, maxHeight: null })
    const rowRef = useRef(null)
    const closeTimer = useRef(null)

    useEffect(() => {
        if (isDirectlyActive || isChildActive) {
            setOpen(true)
        }
    }, [isDirectlyActive, isChildActive])

    useEffect(() => () => clearTimeout(closeTimer.current), [])

    const toggleOpen = (e) => {
        if (hasChildren && !collapsed) {
            e.preventDefault()
            setOpen((prev) => !prev)
        }
        // Ve sbaleném stavu klik neblokujeme — Link naviguje normálně,
        // potomci se ukazují přes hover flyout, ne přes klik.
    }

    const openFlyout = () => {
        if (!collapsed || !hasChildren) return
        clearTimeout(closeTimer.current)
        const rect = rowRef.current?.getBoundingClientRect()
        if (rect) {
            // Dlouhé podmenu (např. "Správa areálu" s 13 položkami) se u řádku blízko
            // spodního okraje nevejde pod top: rect.top — položky by beze stopy zmizely
            // pod okrajem obrazovky. Omez výšku na zbývající prostor a nech scrollovat.
            const margin = 8
            const top = Math.min(rect.top, window.innerHeight - margin)
            const maxHeight = window.innerHeight - top - margin
            setFlyoutPos({ top, left: rect.right + 4, maxHeight })
        }
        setFlyoutOpen(true)
    }

    const scheduleCloseFlyout = () => {
        closeTimer.current = setTimeout(() => setFlyoutOpen(false), 150)
    }

    // Definice stylů podle stavu (světlá/tmavá varianta, viz `dark` výše)
    const accent = dark ? '#5b8def' : '#004ac6'
    let activeStyle = {
        color: dark ? '#a0aec0' : '#505f76',
        borderRadius: '0.5rem'
    }

    if (isDirectlyActive) {
        // A) Přímo vybraná položka (tmavší podbarvení + levý modrý proužek)
        activeStyle = {
            backgroundColor: dark ? 'rgba(91, 141, 239, 0.18)' : 'rgba(208, 225, 251, 0.5)',
            color: accent,
            borderLeft: `4px solid ${accent}`,
            borderTopRightRadius: '0.5rem',
            borderBottomRightRadius: '0.5rem',
            fontWeight: '600'
        }
    } else if (isChildActive) {
        // B) Rodičovská položka (světlejší podbarvení + modré písmo)
        activeStyle = {
            backgroundColor: dark ? 'rgba(91, 141, 239, 0.10)' : 'rgba(231, 238, 255, 0.7)',
            color: accent,
            fontWeight: '600',
            borderRadius: '0.5rem'
        }
    }

    return (
        <div
            ref={rowRef}
            className="w-full"
            onMouseEnter={openFlyout}
            onMouseLeave={collapsed ? scheduleCloseFlyout : undefined}
        >
            <Link
                to={url || '#'}
                onClick={hasChildren ? toggleOpen : undefined}
                title={collapsed ? title : undefined}
                style={activeStyle}
                className={`py-2 flex items-center transition-colors duration-200 text-sm font-medium hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-900 dark:hover:text-blue-300 ${
                    collapsed ? 'px-2 justify-center' : 'px-4 justify-between'
                }`}
            >
                {/* overflow-hidden jen v rozbaleném stavu — je potřeba kvůli truncate
                    na dlouhém textu. Ve sbaleném stavu žádný text není a overflow-hidden
                    by na flex položce srazil min-width na 0 a podřízl by i ikonu. */}
                <div className={`flex items-center ${collapsed ? 'justify-center' : 'overflow-hidden'}`}>
                    {icon && (
                        <span
                            className={`text-lg flex-shrink-0 flex items-center justify-center ${
                                collapsed ? '' : 'mr-3'
                            }`}
                        >
                            {icon}
                        </span>
                    )}
                    {!collapsed && <span className="truncate">{title}</span>}
                </div>

                {hasChildren && !collapsed && (
                    <span className="text-base text-grey-500 dark:text-grey-400 flex-shrink-0 ml-2">
                        {open ? <FiChevronUp /> : <FiChevronDown />}
                    </span>
                )}
            </Link>

            {/* Rozbalený stav: klasický akordeon, beze změny oproti původnímu chování */}
            {hasChildren && !collapsed && open && <div className="mt-1">{children}</div>}

            {/* Sbalený stav: potomci jako flyout panel při hoveru, portálovaný do body */}
            {hasChildren &&
                collapsed &&
                flyoutOpen &&
                createPortal(
                    <div
                        style={{
                            position: 'fixed', top: flyoutPos.top, left: flyoutPos.left, minWidth: 200,
                            maxHeight: flyoutPos.maxHeight || undefined, overflowY: 'auto'
                        }}
                        className="bg-white dark:bg-grey-800 rounded-lg shadow-lg border border-grey-200 dark:border-grey-700 py-2 z-50"
                        onMouseEnter={openFlyout}
                        onMouseLeave={scheduleCloseFlyout}
                    >
                        <div className="px-3 pb-1 text-xs font-semibold text-grey-400 dark:text-grey-500 truncate">{title}</div>
                        {children}
                    </div>,
                    document.body
                )}
        </div>
    )
}

export default Item
